import { Component, ElementRef, computed, input, model, output, viewChild } from '@angular/core'
import { FormValueControl } from '@angular/forms/signals'
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { MatChipsModule } from '@angular/material/chips'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'

/** One row the search offers. */
export interface SearchOrCreateOption<T> {
  value: T
  label: string
  /** A second, muted line: an e-mail, a phone. */
  detail?: string
  /** Shown at the end of the row, which then cannot be chosen ("už je prihlásený"). */
  disabledReason?: string
}

/** Rows under an optional heading ("Odporúčané"); a group without a label is a plain list. */
export interface SearchOrCreateGroup<T> {
  label?: string
  options: readonly SearchOrCreateOption<T>[]
}

/** The value of the first row, which is not an option but the way to create one. */
const CREATE = Symbol('create')

/**
 * The bare search-or-create combobox: an outline field with the chosen values as chips, a `mat-autocomplete` of what
 * the host offers for the typed query, optionally in groups, and above them a row "+ Nový … „{query}“" that emits
 * `create(query)` - first, so it is in reach however many matches there are. `<seiri-search-or-create label="Klienti" [groups]="groups()" [(query)]="query" [display]="name"
 * [key]="id" createLabel="Nový klient" (create)="…" [formField]="form.customers" />`.
 *
 * It knows nothing of what it searches. The host filters (`groups` follows `query`), decides what creating means, and
 * adds what it created to the value. A Signal Forms control (`FormValueControl`), or `[(value)]`; the value is never
 * mutated, every change is a new array.
 *
 * Like a `mat-form-field`, it has no width of its own; its layout sizes it.
 */
@Component({
  selector: 'seiri-search-or-create',
  imports: [MatAutocompleteModule, MatChipsModule, MatFormFieldModule, MatIconModule],
  templateUrl: './search-or-create.component.html',
  styleUrl: './search-or-create.component.css',
  host: {
    class: 'seiri-search-or-create',
  },
})
export class SearchOrCreateComponent<T> implements FormValueControl<T[]> {
  readonly value = model<T[]>([])
  readonly disabled = input(false)
  readonly invalid = input(false)
  readonly touched = input(false)
  readonly touch = output<void>()

  readonly label = input.required<string>()
  readonly placeholder = input('')
  /** What the search offers for the current `query`; the host does the matching. */
  readonly groups = input.required<readonly SearchOrCreateGroup<T>[]>()
  /** How a chosen value reads on its chip. */
  readonly display = input.required<(value: T) => string>()
  /** A stable id of a value, for tracking the chips. */
  readonly key = input.required<(value: T) => string>()
  /** The create row's text, "Nový klient"; empty leaves the row out. */
  readonly createLabel = input('')
  readonly createIcon = input('add')

  /** What is typed; the host filters by it. */
  readonly query = model('')
  /** The create row was chosen, with the query as typed. */
  readonly create = output<string>()

  protected readonly createValue = CREATE
  protected readonly trimmedQuery = computed(() => this.query().trim())
  protected readonly empty = computed(() => this.groups().every((group) => group.options.length === 0))

  private readonly input = viewChild.required<ElementRef<HTMLInputElement>>('input')

  protected onInput(event: Event) {
    this.query.set((event.target as HTMLInputElement).value)
  }

  protected choose(event: MatAutocompleteSelectedEvent) {
    const chosen = event.option.value as T | typeof CREATE
    if (chosen === CREATE) {
      this.create.emit(this.trimmedQuery())
    } else {
      this.value.set([...this.value(), chosen])
    }
    // The autocomplete writes the option into the input; the query starts over for the next one.
    this.input().nativeElement.value = ''
    this.query.set('')
  }

  protected remove(removed: T) {
    const key = this.key()
    this.value.set(this.value().filter((value) => key(value) !== key(removed)))
    this.touch.emit()
  }
}
